import React, { useEffect, useState } from "react";
import { Box, Collapse, Typography } from "@mui/material";

import { UavNotificationStamped } from "../features/aircraft/types";
import { useInterval } from "../useInterval";
import { TransitionGroup } from "react-transition-group";

const maxNotifications = 4;
const blacklist = ["DELIVERY_CMD_WAIT"];

export const NotificationTracker = (props: {
  uavId: string;
  notifications: UavNotificationStamped[];
}) => {
  const [_blank, setUpdate] = useState<number>(0);

  useInterval(() => {
    setUpdate(Date.now());
  }, 1000);

  const secsAgo = (timestamp: number) => {
    const now = Date.now();
    const diff = now - timestamp;
    const diffInSecs = diff / 1000;

    return diffInSecs;
  };

  const getDisplayTime = (level: number) => {
    if (level === 5) return 50;
    if (level === 4) return 50;
    if (level === 3) return 30;
    return 10;
  };

  const getNotificationsToDisplay = () => {
    const lowLevelNotifications = [...props.notifications]
      .filter((notif) => !blacklist.includes(notif.type))
      .filter((notif) => notif.level < 3)
      .filter(
        (notif) =>
          !notif.type.includes("DROP_PHASE_CHANGE_") &&
          !notif.type.includes("PHASE_CHANGE_")
      )
      .filter((notif) => secsAgo(notif.timestamp) < getDisplayTime(notif.level))
      .slice(0, maxNotifications - 1);

    const highLevelNotifications = [...props.notifications]
      .filter((notif) => notif.level > 2)
      .filter(
        (notif) => secsAgo(notif.timestamp) < getDisplayTime(notif.level)
      );

    return [...lowLevelNotifications, ...highLevelNotifications].sort(
      (a, b) => b.timestamp - a.timestamp
    );
  };
  const notificationsToDisplay = getNotificationsToDisplay();

  if (!props.notifications.length) return null;
  const readableMinsAgo = (timestamp: number) => {
    const now = Date.now();
    const diff = now - timestamp;
    const diffInSecs = diff / 1000;
    const diffInMins = diffInSecs / 60;
    if (diffInSecs < 5) {
      return `now`;
    }
    if (diffInSecs > 60) {
      return `${diffInMins.toFixed(0)} mins ago`;
    } else {
      return `${diffInSecs.toFixed(0)} secs ago`;
    }
  };

  const notificationColour = (notification: UavNotificationStamped) => {
    if (notification.info) return "#FFFFFF";
    if (notification.level === 5) return "#ED0505";
    if (notification.level === 4) return "#FAFF00";
    if (notification.level === 3) return "#FAFF00";
    if (notification.level === 2) return "#FFFFFF";
    return "#FFFFFF";
  };

  return (
    <Box display={"flex"} flexDirection={"column"}>
      <TransitionGroup>
        {notificationsToDisplay.map((notification, i) => {
          const isLast = i === notificationsToDisplay.length - 1;
          const warningColour = notificationColour(notification);

          return (
            <Collapse key={`${notification.type}-${props.uavId}`}>
              <Box flexDirection={"row"} display={"flex"}>
                <Box
                  flexDirection={"column"}
                  alignContent="center"
                  bgcolor={"transparent"}
                  display={"flex"}
                  sx={{ width: 80, height: 60 }}
                >
                  <Box
                    sx={{ width: 2, height: 19 }}
                    alignSelf="center"
                    bgcolor={warningColour}
                    ml={1.4}
                  />
                  <Box
                    alignSelf="center"
                    ml={1.4}
                    sx={{
                      width: 10,
                      height: 10,
                      borderRadius: 10,
                      border: `2px solid ${warningColour}`,
                    }}
                  />
                  {isLast ? null : (
                    <Box
                      alignSelf="center"
                      ml={1.4}
                      sx={{ width: 2, height: 32 }}
                      bgcolor={warningColour}
                    />
                  )}
                </Box>
                <Box
                  pt={0.5}
                  sx={{ width: 258 }}
                  key={notification.type}
                  display={"flex"}
                  flexDirection={"column"}
                  justifyContent="start"
                >
                  <Box position={"relative"} width={"100%"} height={34}>
                    <Typography
                      variant="h4"
                      textAlign={"start"}
                      color={warningColour}
                      fontWeight={500}
                      fontSize={14}
                      sx={{ position: "absolute", bottom: 0 }}
                    >
                      {notification.msg}
                    </Typography>
                  </Box>
                  <Typography
                    variant="body2"
                    textAlign={"start"}
                    fontSize={11}
                    lineHeight={"21px"}
                  >
                    {readableMinsAgo(notification.timestamp)}
                  </Typography>
                </Box>
              </Box>
            </Collapse>
          );
        })}
      </TransitionGroup>
    </Box>
  );
};
